import { influencer } from '@round/api';
import { useAggregatedPostStats } from '@round/creatorbase-helpers/api-hooks';
import usePublicReportData from '../ReportData/usePublicReportData';
import HeadlineStats, { Stats } from 'Modules/Plans/components/HeadlineStats/HeadlineStats';
import { asMoneyWithoutZeros, formatNumberToKNotation } from 'helpers';
import { platformLabels, tooltipCopy } from 'Modules/Plans/helpers';
import { toDecimalPoint } from '@round/utils';
import { useCurrencies } from 'contexts/OptionsContext/useCurrencies';

type Props = {
    publicReportId: string | undefined;
    platform: influencer.Platform;
};

const AggregatedPlatformStats = ({ platform, publicReportId }: Props) => {
    const { data: report, status: reportStatus } = usePublicReportData();
    const { getCurrencyById } = useCurrencies();
    const isReportInitialized = reportStatus === 'success' || reportStatus === 'error';
    const isEnabled = isReportInitialized && !!report?.platforms.includes(platform) && !!publicReportId;

    const { data, isLoading: isStatsLoading } = useAggregatedPostStats(
        isEnabled ? { platform, public_report_id: publicReportId, publicReportId } : null
    );

    const isLoading = !isReportInitialized || isStatsLoading;

    // There should only be one currency, so take the first entry.
    const totalBudgetCurrencyValuePair = Object.entries(data?.total_budget || {})[0] || [];
    const totalCost = totalBudgetCurrencyValuePair.length
        ? asMoneyWithoutZeros(totalBudgetCurrencyValuePair[1], getCurrencyById(Number(totalBudgetCurrencyValuePair[0])))
        : null;

    const stats: Stats = [
        {
            label: 'Total Posts',
            value: data?.post_count,
        },
        {
            label: 'Total Followers',
            value: typeof data?.follower_count === 'number' ? formatNumberToKNotation(data.follower_count) : '-',
        },
        {
            label: 'Total Views',
            value: typeof data?.view_count === 'number' ? formatNumberToKNotation(data.view_count) : '-',
        },
        {
            label: 'Engagement Rate',
            value:
                typeof data?.engagement_rate === 'number' ? `${toDecimalPoint(data.engagement_rate * 100, 2)}%` : '-',
            tooltip: tooltipCopy.engagementRate,
        },
        ...(totalCost !== null
            ? [
                  {
                      label: `${platformLabels[platform]} Cost`,
                      value: totalCost,
                  },
              ]
            : []),
    ];

    return <HeadlineStats stats={stats} isLoading={isLoading} />;
};

export default AggregatedPlatformStats;
